/**
 * BillingPage - Stránka pro nákup kreditů a historii plateb
 *
 * Integrace Stripe Checkout pro platby kartou.
 * Design odpovídá Phoenix Premium stylu.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserCredits } from '../../services/userService';
import {
  createCheckoutSession,
  getTransactions,
  downloadInvoice,
  verifySession,
  formatDate,
  formatAmount,
  Transaction,
} from '../../services/billingService';

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const CandidateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const AdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const CoinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v12"/>
    <path d="M15 9.5c-.5-1-1.5-1.5-3-1.5s-3 1-3 2.5c0 1.5 1.5 2 3 2.5s3 1 3 2.5c0 1.5-1.5 2.5-3 2.5s-2.5-.5-3-1.5"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const LoadingSpinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

const BillingPage: React.FC = () => {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load user credits
  useEffect(() => {
    const loadCredits = async () => {
      if (user?.uid) {
        const userCredits = await getUserCredits(user.uid);
        setCredits(userCredits);
      }
    };
    loadCredits();
  }, [user?.uid]);

  // Load transactions
  const loadTransactions = useCallback(async () => {
    if (!user) return;

    try {
      setLoadingTransactions(true);
      const txns = await getTransactions({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      });
      setTransactions(txns);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [user]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Handle Stripe redirect (success/cancel)
  useEffect(() => {
    const handleStripeRedirect = async () => {
      const success = searchParams.get('success');
      const canceled = searchParams.get('canceled');
      const sessionId = searchParams.get('session_id');

      if (success === 'true' && sessionId && user) {
        try {
          const result = await verifySession(
            { uid: user.uid, email: user.email, displayName: user.displayName },
            sessionId
          );

          if (result.payment_status === 'paid') {
            setSuccessMessage(`Platba proběhla úspěšně! Přičteno ${result.credits_purchased} kredit${result.credits_purchased > 1 ? 'ů' : ''}.`);
            // Reload credits and transactions
            const newCredits = await getUserCredits(user.uid);
            setCredits(newCredits);
            await loadTransactions();
          }
        } catch (err) {
          console.error('Failed to verify session:', err);
        }

        // Clear URL params
        window.history.replaceState({}, '', '/billing');
      } else if (canceled === 'true') {
        setError('Platba byla zrušena.');
        window.history.replaceState({}, '', '/billing');
      }
    };

    handleStripeRedirect();
  }, [searchParams, user, loadTransactions]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleBuyCredit = async () => {
    if (!user) return;

    try {
      setLoadingCheckout(true);
      setError(null);

      const result = await createCheckoutSession(
        { uid: user.uid, email: user.email, displayName: user.displayName },
        1
      );

      // Redirect to Stripe Checkout
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Nepodařilo se vytvořit platební sezení.');
      setLoadingCheckout(false);
    }
  };

  const handleDownloadInvoice = async (transaction: Transaction) => {
    if (!user || !transaction.invoice_number) return;

    try {
      await downloadInvoice(
        { uid: user.uid, email: user.email, displayName: user.displayName },
        transaction.id,
        transaction.invoice_number
      );
    } catch (err) {
      console.error('Download error:', err);
      setError('Nepodařilo se stáhnout fakturu.');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-500) 100%)',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                }}
              >
                <LogoIcon />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white tracking-tight">
                  Phoenix Analytics
                </h1>
                <p className="text-xs text-white/60">Volební analýza v6.6</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/filter"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <ChartIcon />
                  Analýza
                </span>
              </Link>
              <Link
                to="/candidate"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <CandidateIcon />
                  Kandidát
                </span>
              </Link>
              <Link
                to="/billing"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white"
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
              >
                <span className="flex items-center gap-2">
                  <CreditCardIcon />
                  Platby
                </span>
              </Link>
              <Link
                to="/help"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <span className="flex items-center gap-2">
                  <HelpIcon />
                  Nápověda
                </span>
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
                >
                  <span className="flex items-center gap-2">
                    <AdminIcon />
                    Admin
                  </span>
                </Link>
              )}
            </nav>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-white/10"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <UserIcon />
                </div>
                <span className="hidden sm:block text-sm text-white/90 max-w-[150px] truncate">
                  {user?.email}
                </span>
                <svg
                  className={`w-4 h-4 text-white/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {/* Dropdown */}
              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Přihlášen jako
                    </p>
                    <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                      {user?.email}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Kredity:
                      </span>
                      <span
                        className="px-2 py-0.5 rounded-full text-sm font-semibold"
                        style={{
                          background: isAdmin ? 'var(--accent-primary)' : (credits > 0 ? '#10B981' : '#EF4444'),
                          color: 'white'
                        }}
                      >
                        {isAdmin ? '∞' : credits}
                      </span>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-red-50"
                      style={{ color: 'var(--danger-600)' }}
                    >
                      <LogoutIcon />
                      Odhlásit se
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-white/10">
          <div className="flex">
            <Link
              to="/filter"
              className="flex-1 py-3 text-center text-sm font-medium text-white/70"
            >
              Analýza
            </Link>
            <Link
              to="/candidate"
              className="flex-1 py-3 text-center text-sm font-medium text-white/70"
            >
              Kandidát
            </Link>
            <Link
              to="/billing"
              className="flex-1 py-3 text-center text-sm font-medium text-white bg-white/10"
            >
              Platby
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="flex-1 py-3 text-center text-sm font-medium text-white/70"
              >
                Admin
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2
            className="font-display text-2xl lg:text-3xl font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Kredity a platby
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Zakupte kredity pro spouštění volebních analýz.
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div
            className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{
              background: 'var(--success-50)',
              border: '1px solid var(--success-200)'
            }}
          >
            <CheckIcon />
            <p style={{ color: 'var(--success-700)' }}>{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-700 hover:text-green-900"
            >
              &times;
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{
              background: 'var(--danger-50)',
              border: '1px solid var(--danger-200)'
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--danger-600)" strokeWidth="2" className="w-5 h-5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <p style={{ color: 'var(--danger-700)' }}>{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-700 hover:text-red-900"
            >
              &times;
            </button>
          </div>
        )}

        {/* Current Credits Card */}
        <div
          className="rounded-2xl p-6 mb-8"
          style={{
            background: 'linear-gradient(135deg, var(--phoenix-600) 0%, var(--phoenix-800) 100%)',
            boxShadow: '0 10px 40px rgba(0, 70, 140, 0.3)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm mb-1">Váš aktuální stav</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white">
                  {isAdmin ? '∞' : credits}
                </span>
                <span className="text-white/70">
                  {isAdmin ? '' : (credits === 1 ? 'kredit' : (credits >= 2 && credits <= 4 ? 'kredity' : 'kreditů'))}
                </span>
              </div>
              {!isAdmin && credits === 0 && (
                <p className="text-white/60 text-sm mt-2">
                  Pro spuštění analýzy potřebujete alespoň 1 kredit
                </p>
              )}
            </div>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-white"
              style={{ background: 'rgba(255, 255, 255, 0.15)' }}
            >
              <CoinIcon />
            </div>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Buy Credits */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <h3
              className="font-display text-xl font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Koupit kredit
            </h3>

            {/* Price */}
            <div
              className="rounded-xl p-6 mb-6 text-center"
              style={{ background: 'var(--bg-tertiary)' }}
            >
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span
                  className="text-4xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  1 000
                </span>
                <span
                  className="text-xl"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Kč
                </span>
              </div>
              <p style={{ color: 'var(--text-secondary)' }}>
                za 1 kredit
              </p>
              <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                Neplátce DPH
              </p>
            </div>

            {/* Features */}
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                >
                  <CheckIcon />
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  1 kredit = 1 kompletní analýza
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                >
                  <CheckIcon />
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  HTML dashboard + PDF report
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                >
                  <CheckIcon />
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Strategická doporučení od AI
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                >
                  <CheckIcon />
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Doklad o zaplacení automaticky
                </span>
              </li>
            </ul>

            {/* Buy Button */}
            <button
              onClick={handleBuyCredit}
              disabled={loadingCheckout}
              className="w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              style={{
                background: loadingCheckout ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)',
                color: 'white',
                cursor: loadingCheckout ? 'not-allowed' : 'pointer',
                boxShadow: loadingCheckout ? 'none' : '0 4px 15px rgba(0, 70, 140, 0.3)'
              }}
            >
              {loadingCheckout ? (
                <>
                  <LoadingSpinner />
                  Přesměrování na platbu...
                </>
              ) : (
                <>
                  <CreditCardIcon />
                  Zaplatit kartou
                </>
              )}
            </button>

            <p
              className="text-center text-sm mt-3"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Budete přesměrováni na zabezpečenou platební stránku Stripe
            </p>
          </div>

          {/* How it works */}
          <div
            className="rounded-2xl p-6"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <h3
              className="font-display text-xl font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Jak to funguje?
            </h3>

            <div
              className="rounded-xl p-4"
              style={{
                background: 'var(--phoenix-50)',
                border: '1px solid var(--phoenix-200)'
              }}
            >
              <ol className="space-y-3 text-sm" style={{ color: 'var(--phoenix-700)' }}>
                <li className="flex gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--phoenix-200)', color: 'var(--phoenix-800)' }}
                  >1</span>
                  <span>Klikněte na "Zaplatit kartou"</span>
                </li>
                <li className="flex gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--phoenix-200)', color: 'var(--phoenix-800)' }}
                  >2</span>
                  <span>Vyplňte údaje karty na Stripe</span>
                </li>
                <li className="flex gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--phoenix-200)', color: 'var(--phoenix-800)' }}
                  >3</span>
                  <span>Kredit se automaticky přičte k vašemu účtu</span>
                </li>
                <li className="flex gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--phoenix-200)', color: 'var(--phoenix-800)' }}
                  >4</span>
                  <span>Fakturu si můžete stáhnout níže</span>
                </li>
              </ol>
            </div>

            <div
              className="rounded-xl p-4 mt-4 flex items-center gap-3"
              style={{
                background: 'var(--success-50)',
                border: '1px solid var(--success-200)'
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--success-600)" strokeWidth="2" className="w-5 h-5 flex-shrink-0">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <p className="text-sm" style={{ color: 'var(--success-700)' }}>
                Bezpečná platba přes Stripe
              </p>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div
          className="rounded-2xl p-6"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}
        >
          <h3
            className="font-display text-xl font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Historie plateb
          </h3>

          {loadingTransactions ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>
                Načítám historii...
              </span>
            </div>
          ) : transactions.length === 0 ? (
            /* Empty State */
            <div
              className="rounded-xl p-8 text-center"
              style={{ background: 'var(--bg-tertiary)' }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}
              >
                <CreditCardIcon />
              </div>
              <p className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Zatím žádné platby
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Po zakoupení kreditů zde uvidíte historii vašich plateb a faktury ke stažení.
              </p>
            </div>
          ) : (
            /* Transactions Table */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Datum
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Kredity
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Částka
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Stav
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Faktura
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      style={{ borderBottom: '1px solid var(--border-light)' }}
                    >
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {formatDate(tx.created_at)}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                        +{tx.credits_purchased}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {formatAmount(tx.amount_czk)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: tx.status === 'completed' ? 'var(--success-100)' : 'var(--warning-100)',
                            color: tx.status === 'completed' ? 'var(--success-700)' : 'var(--warning-700)'
                          }}
                        >
                          {tx.status === 'completed' ? 'Zaplaceno' : tx.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {tx.invoice_number ? (
                          <button
                            onClick={() => handleDownloadInvoice(tx)}
                            className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
                            style={{ color: 'var(--phoenix-600)' }}
                          >
                            <DownloadIcon />
                            {tx.invoice_number}
                          </button>
                        ) : (
                          <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="py-6 mt-12"
        style={{
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-light)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center">
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            © 2025 Phoenix Analytics
          </p>
          <a
            href="mailto:pecka@dwx.cz"
            className="text-sm mt-1 inline-block transition-colors"
            style={{ color: 'var(--phoenix-500)' }}
          >
            📧 pecka@dwx.cz
          </a>
        </div>
      </footer>
    </div>
  );
};

export default BillingPage;
